import { useController, useWatch, type Control } from "react-hook-form";

import type { SectionSpecItem } from "@/shared/models/SectionSpec";
import {
	DIMENSION_READING_VALIDATION_STATUS,
	validateMeasurement,
} from "@/shared/utils/measurementPrecision";
import {
	type InspectionReadingPayload,
	legDeltas,
} from "../../models/Inspection";
import type { InspectionFormValues } from "../../models/InspectionForm";
import {
	readingKey,
	REQUIRED_DIMENSION_MESSAGE,
} from "../../utils/inspectionFormValues";

const LEG_CODES = ["ALT", "RLT", "ALB", "RLB"] as const;

const applicableDimensions = (items: SectionSpecItem[]) =>
	items.filter((item) => item.Applies && item.ValueTypeCode === "V");

/** Readings that pass validation, ready to save. */
export const getValidReadings = (
	items: SectionSpecItem[],
	readings: InspectionFormValues["readings"],
): InspectionReadingPayload[] =>
	applicableDimensions(items).flatMap((item) => {
		const validation = validateMeasurement(
			readings[readingKey(item.ProfileItemId)] ?? "",
		);
		return validation.status === DIMENSION_READING_VALIDATION_STATUS.VALID &&
			validation.value !== null
			? [{ ProfileItemId: item.ProfileItemId, NumericValue: validation.value }]
			: [];
	});

/**
 * Watches only the four leg readings, so typing any other dimension doesn't
 * re-render the components that show the deltas.
 */
export const useLegDeltas = (
	control: Control<InspectionFormValues>,
	items: SectionSpecItem[],
	legDeltaMax: number | null,
) => {
	const legItems = LEG_CODES.map((code) =>
		applicableDimensions(items).find((item) => item.Code === code),
	);
	const values = useWatch({
		control,
		name: legItems.map(
			(item) => `readings.${readingKey(item?.ProfileItemId ?? 0)}` as const,
		),
	});
	const measured: Record<string, number> = {};
	legItems.forEach((item, index) => {
		if (!item) return;
		const validation = validateMeasurement(values[index] ?? "");
		if (
			validation.status === DIMENSION_READING_VALIDATION_STATUS.VALID &&
			validation.value !== null
		)
			measured[item.Code] = validation.value;
	});
	const deltas = legDeltas(measured);
	return {
		topDelta: deltas.top,
		bottomDelta: deltas.bottom,
		legDeltaMax,
		topOut:
			deltas.top !== null && legDeltaMax !== null && deltas.top > legDeltaMax,
		bottomOut:
			deltas.bottom !== null &&
			legDeltaMax !== null &&
			deltas.bottom > legDeltaMax,
	};
};

/** Registers the "at least one dimension" rule without subscribing the form to every reading. */
export const ReadingsValidator = ({
	control,
	items,
	getReadings,
}: {
	control: Control<InspectionFormValues>;
	items: SectionSpecItem[];
	getReadings: () => InspectionFormValues["readings"];
}) => {
	useController({
		name: "readings",
		control,
		rules: {
			validate: () =>
				getValidReadings(items, getReadings()).length > 0 ||
				REQUIRED_DIMENSION_MESSAGE,
		},
	});
	return null;
};
